import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { bottlesApi } from '../../lib/api';
import { FormField, Input, Select, Textarea } from '../../components/ui/FormField';
import { AlertBanner } from '../../components/ui/AlertBanner';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
const schema = z.object({
    overallCondition: z.string().min(1, 'Overall condition is required'),
    inspectionDate: z.string().min(1, 'Date is required'),
    fillLevelCode: z.string().optional(),
    closureType: z.string().optional(),
    frontLabelCondition: z.string().optional(),
    backLabelCondition: z.string().optional(),
    capsuleHasCorrosion: z.boolean().optional(),
    seepageEvident: z.boolean().optional(),
    sedimentVisible: z.boolean().optional(),
    provenanceGrade: z.string().optional(),
    notes: z.string().optional(),
});
const CONDITION_GRADES = [
    { value: 'excellent', label: 'Excellent' },
    { value: 'very_good', label: 'Very Good' },
    { value: 'good', label: 'Good' },
    { value: 'fair', label: 'Fair' },
    { value: 'poor', label: 'Poor' },
    { value: 'damaged', label: 'Damaged' },
];
const FILL_LEVELS = [
    { value: '', label: 'Not recorded' },
    { value: 'BN', label: 'BN – Bottle neck' },
    { value: 'IN', label: 'IN – Into neck' },
    { value: 'VTS', label: 'VTS – Very top shoulder' },
    { value: 'TS', label: 'TS – Top shoulder' },
    { value: 'US', label: 'US – Upper shoulder' },
    { value: 'HS', label: 'HS – High shoulder' },
    { value: 'MS', label: 'MS – Mid shoulder' },
    { value: 'LS', label: 'LS – Low shoulder' },
    { value: 'BE', label: 'BE – Below elbow' },
];
const CLOSURE_TYPES = [
    { value: '', label: 'Not recorded' },
    { value: 'natural_cork', label: 'Natural cork' },
    { value: 'technical_cork', label: 'Technical cork' },
    { value: 'diam', label: 'DIAM' },
    { value: 'stelvin', label: 'Stelvin (screw cap)' },
    { value: 'glass_stopper', label: 'Glass stopper' },
    { value: 'crown_cap', label: 'Crown cap' },
    { value: 'wax_seal', label: 'Wax seal' },
];
const LABEL_CONDITIONS = [
    { value: '', label: 'Not recorded' },
    { value: 'perfect', label: 'Perfect' },
    { value: 'excellent', label: 'Excellent' },
    { value: 'good', label: 'Good' },
    { value: 'fair', label: 'Fair' },
    { value: 'poor', label: 'Poor' },
    { value: 'missing', label: 'Missing' },
    { value: 'unknown', label: 'Unknown' },
];
const PROVENANCE_GRADES = [
    { value: '', label: 'Not assessed' },
    { value: 'excellent', label: 'Excellent – Impeccable provenance' },
    { value: 'good', label: 'Good – Well documented' },
    { value: 'fair', label: 'Fair – Acceptable provenance' },
    { value: 'poor', label: 'Poor – Questionable history' },
    { value: 'uncertain', label: 'Uncertain – Unknown history' },
];
export function ConditionInspectionForm({ bottleId, onSuccess, onCancel }) {
    const [error, setError] = useState(null);
    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            inspectionDate: new Date().toISOString().slice(0, 10),
            capsuleHasCorrosion: false,
            seepageEvident: false,
            sedimentVisible: false,
        },
    });
    const mutation = useMutation({
        mutationFn: (data) => bottlesApi.addCondition(bottleId, {
            overallCondition: data.overallCondition,
            inspectionDate: data.inspectionDate,
            fillLevelCode: data.fillLevelCode || undefined,
            closureType: data.closureType || undefined,
            frontLabelCondition: data.frontLabelCondition || undefined,
            backLabelCondition: data.backLabelCondition || undefined,
            capsuleHasCorrosion: Boolean(data.capsuleHasCorrosion),
            seepageEvident: Boolean(data.seepageEvident),
            sedimentVisible: Boolean(data.sedimentVisible),
            provenanceGrade: data.provenanceGrade || undefined,
            conditionNotes: data.notes || undefined,
        }),
        onSuccess,
        onError: (e) => setError(e.message),
    });
    return (_jsxs("form", { onSubmit: handleSubmit((d) => mutation.mutate(d)), className: "space-y-4", children: [error && _jsx(AlertBanner, { type: "error", title: error, onDismiss: () => setError(null) }), _jsxs("div", { className: "grid grid-cols-2 gap-3", children: [_jsx(FormField, { label: "Overall condition", id: "condition", required: true, error: errors.overallCondition?.message, children: _jsx(Select, { id: "condition", options: CONDITION_GRADES, placeholder: "Select grade", error: Boolean(errors.overallCondition), ...register('overallCondition') }) }), _jsx(FormField, { label: "Inspection date", id: "inspDate", required: true, children: _jsx(Input, { id: "inspDate", type: "date", ...register('inspectionDate') }) })] }), _jsxs("div", { className: "grid grid-cols-2 gap-3", children: [_jsx(FormField, { label: "Fill level", id: "fillLevel", children: _jsx(Select, { id: "fillLevel", options: FILL_LEVELS, ...register('fillLevelCode') }) }), _jsx(FormField, { label: "Closure type", id: "closure", children: _jsx(Select, { id: "closure", options: CLOSURE_TYPES, ...register('closureType') }) })] }), _jsxs("div", { className: "grid grid-cols-2 gap-3", children: [_jsx(FormField, { label: "Front label", id: "frontLabel", children: _jsx(Select, { id: "frontLabel", options: LABEL_CONDITIONS, ...register('frontLabelCondition') }) }), _jsx(FormField, { label: "Back label", id: "backLabel", children: _jsx(Select, { id: "backLabel", options: LABEL_CONDITIONS, ...register('backLabelCondition') }) })] }), _jsx(FormField, { label: "Provenance grade", id: "provenance", children: _jsx(Select, { id: "provenance", options: PROVENANCE_GRADES, ...register('provenanceGrade') }) }), _jsx("div", { className: "grid grid-cols-3 gap-4 py-2", children: [
                    { id: 'capsuleCorrosion', field: 'capsuleHasCorrosion', label: 'Capsule corrosion' },
                    { id: 'seepage', field: 'seepageEvident', label: 'Seepage evident' },
                    { id: 'sediment', field: 'sedimentVisible', label: 'Sediment visible' },
                ].map(({ id, field, label }) => (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("input", { id: id, type: "checkbox", className: "h-4 w-4 rounded border-gray-300 text-wine-700 focus:ring-wine-600", ...register(field) }), _jsx("label", { htmlFor: id, className: "text-sm text-gray-700", children: label })] }, id))) }), _jsx(FormField, { label: "Notes", id: "condNotes", children: _jsx(Textarea, { id: "condNotes", rows: 3, placeholder: "Any additional observations\u2026", ...register('notes') }) }), _jsxs("div", { className: "flex items-center justify-end gap-3 pt-2", children: [_jsx("button", { type: "button", onClick: onCancel, className: "btn-secondary", children: "Cancel" }), _jsx("button", { type: "submit", disabled: mutation.isPending, className: "btn-primary", children: mutation.isPending ? _jsx(LoadingSpinner, { size: "sm" }) : 'Save inspection' })] })] }));
}
